import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

export type EscalationStatus = "idle" | "waiting" | "human-active" | "closed";

export interface PendingCodeSearch {
  noticeTopic: string;
  threadId: string;
}

export interface EscalationState {
  escalationId: string | null;
  intercomConversationId: string | null;
  noticeTopic: string | null;
  status: EscalationStatus;
  codeSearchAttempted: boolean;
  pendingCodeSearch: PendingCodeSearch | null;
}

export interface EscalationContextType {
  escalation: EscalationState;
  setEscalation: (escalation: Partial<EscalationState>) => void;
  clearEscalation: () => void;
  triageThreadId: string | null;
  setTriageThreadId: (id: string | null) => void;
  navigateToThread: ((threadId: string) => void) | null;
  setNavigateToThread: (fn: ((threadId: string) => void) | null) => void;
}

const defaultState: EscalationState = {
  escalationId: null,
  intercomConversationId: null,
  noticeTopic: null,
  status: "idle",
  codeSearchAttempted: false,
  pendingCodeSearch: null,
};

export const EscalationContext = createContext<
  EscalationContextType | undefined
>(undefined);

export function useEscalationContext(): EscalationContextType {
  return useRequiredContext(EscalationContext);
}

interface EscalationProviderProps {
  children: ReactNode;
}

export const EscalationProvider = memo(function EscalationProvider({
  children,
}: EscalationProviderProps) {
  const [escalation, setEscalationState] =
    useState<EscalationState>(defaultState);
  const [triageThreadId, setTriageThreadIdState] = useState<string | null>(
    null,
  );
  const [navigateToThread, setNavigateToThreadState] = useState<
    ((threadId: string) => void) | null
  >(null);

  // Wrap setTriageThreadId so switching threads resets codeSearchAttempted.
  // Ensures each new triage thread gets a fresh shot at code search.
  const setTriageThreadId = useCallback((id: string | null) => {
    setTriageThreadIdState((prev) => {
      if (prev !== id) {
        setEscalationState((prevEscalation) => ({
          ...prevEscalation,
          codeSearchAttempted: false,
          pendingCodeSearch: null,
        }));
      }
      return id;
    });
  }, []);

  const setNavigateToThread = useCallback(
    (fn: ((threadId: string) => void) | null) => {
      setNavigateToThreadState(() => fn);
    },
    [],
  );

  const setEscalation = useCallback((update: Partial<EscalationState>) => {
    setEscalationState((prev) => ({ ...prev, ...update }));
  }, []);

  const clearEscalation = useCallback(() => {
    setEscalationState(defaultState);
  }, []);

  const contextValue = useMemo(
    () => ({
      escalation,
      setEscalation,
      clearEscalation,
      triageThreadId,
      setTriageThreadId,
      navigateToThread,
      setNavigateToThread,
    }),
    [
      escalation,
      setEscalation,
      clearEscalation,
      triageThreadId,
      setTriageThreadId,
      navigateToThread,
      setNavigateToThread,
    ],
  );

  return (
    <EscalationContext.Provider value={contextValue}>
      {children}
    </EscalationContext.Provider>
  );
});
